<template>
  <div
    class="modal-background"
    :class="{
      'is-visible': isModalBackgroundNodeVisible,
      'is-present': isModalBackgroundNodePresent
    }"
    @transitionend="modalBackgroundNodeTransitionEnded"
  ></div>
  <div
    class="modal"
    :class="{ 'is-open': isModalContentNodeVisible }"
    @click="handleModalClicked()"
    @transitionend="modalNodeTransitionEnded"
  >
    <div
      class="modal-content"
      :class="{
        'wide-modal': isWideModal
      }"
    >
      <div
        v-show="modalTitle && fontAwesomeIconClass"
        class="modal-header"
      >
        <span class="header-icon-and-title">
          <i :class="fontAwesomeIconClass"></i>
          {{ modalTitle }}
        </span>
        <button
          type="button"
          title="Close"
          class="modal-close-button"
          @click="closeModal()"
        >
          <i class="fas fa-fw fa-times-circle fa-fw"></i>
        </button>
      </div>
      <div
        class="modal-body"
        :class="{ 'padded-modal-body': shouldAddContentPadding }"
        @click="handleModalBodyClicked()"
      >
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, toRefs, computed, watch, nextTick } from 'vue';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useModals } from '@/composables/useModals';
  import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';

  interface ModalProps {
    modalTypes: string[];
    fontAwesomeIconClass?: string;
    modalTitle?: string;
    shouldAddContentPadding?: boolean;
    shouldCloseModalOnBackgroundClick?: boolean;
    isWideModal?: boolean;
  }

  const layoutStore = useLayoutStore();
  const { closeModal } = useModals();
  const props = withDefaults(defineProps<ModalProps>(), {
    fontAwesomeIconClass: '',
    modalTitle: '',
    shouldAddContentPadding: true,
    shouldCloseModalOnBackgroundClick: true,
    isWideModal: false
  });
  const {
    modalTypes,
    fontAwesomeIconClass,
    modalTitle,
    shouldAddContentPadding,
    shouldCloseModalOnBackgroundClick,
    isWideModal
  } = toRefs(props);
  const isModalBackgroundNodePresent = ref(false);
  const isModalBackgroundNodeVisible = ref(false);
  const isModalContentNodeVisible = ref(false);
  const isOpen = computed(() => {
    return modalTypes.value.includes(layoutStore.modalType);
  });

  let wasModalBodyClicked = false;

  function modalBackgroundNodeTransitionEnded() {
    if (isOpen.value) {
      isModalContentNodeVisible.value = true;
      return;
    }

    isModalBackgroundNodePresent.value = false;
  }

  function modalNodeTransitionEnded() {
    if (!isOpen.value) {
      isModalBackgroundNodeVisible.value = false;
    }
  }

  function handleModalClicked() {
    if (
      !isModalBackgroundNodePresent.value ||
      !isModalContentNodeVisible.value ||
      !shouldCloseModalOnBackgroundClick.value ||
      wasModalBodyClicked
    ) {
      return;
    }

    window.setTimeout(() => {
      if (wasModalBodyClicked) {
        return;
      }

      closeModal();
    }, 0);
  }

  function handleModalBodyClicked() {
    wasModalBodyClicked = true;

    window.setTimeout(() => {
      wasModalBodyClicked = false;
    }, 0);
  }

  async function handleIsOpenChanged() {
    if (!isOpen.value) {
      isModalContentNodeVisible.value = false;
      return;
    }

    isModalBackgroundNodePresent.value = true;

    await nextTick();
    await waitForDomToRepaint();

    if (!isOpen.value) {
      return;
    }

    isModalBackgroundNodeVisible.value = true;
  }

  watch(isOpen, handleIsOpenChanged);
</script>

<style scoped>
  .modal {
    position: fixed;
    top: 0px;
    right: 0px;
    left: 0px;
    bottom: 0px;
    z-index: 1000;
    pointer-events: none;
    opacity: 0;
    transition: opacity 175ms ease;
    color: #212529;
    font-size: 16px;
    font-weight: 400;
    user-select: none;
    line-height: 1.5;
    text-align: left;
  }

  .modal.is-open {
    pointer-events: all;
    opacity: 1;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .modal-background {
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 1000;
    background-color: #333;
    opacity: 0;
    transition: opacity 150ms linear;
    display: none;
  }

  .modal-background.is-present {
    display: block;
  }

  .modal-background.is-visible {
    opacity: 0.5;
  }

  .modal-content {
    position: relative;
    margin: 65px auto 30px auto;
    max-width: 525px;
    transform: translate(0, -25%);
    overflow: visible;
    border-radius: 6px;
    background-color: white;
    box-shadow: 0 5px 15px rgb(0 0 0 / 50%);
    transition: transform 300ms ease-out;
  }

  .modal-content.wide-modal {
    max-width: 600px;
  }

  .modal.is-open .modal-content {
    transform: translate(0, 0);
  }

  .modal-header {
    position: relative;
    height: 45px;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 900;
    font-size: 20px;
    line-height: 1;
    padding: 10px;
    vertical-align: middle;
    background-color: #434c56;
    color: white;
    border-radius: 6px 6px 0px 0px;
  }

  .modal-body {
    padding: 0px;
    overflow: visible;
    border-radius: 6px;
  }

  .padded-modal-body {
    padding: 15px;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }

  .header-icon-and-title {
    vertical-align: middle;
  }

  .modal-close-button {
    position: absolute;
    top: 5px;
    right: 0px;
    color: #a9b2bc;
    font-size: 20px;
    font-weight: 900;
    background: inherit;
    border: none;
  }

  .modal-close-button:hover {
    color: white;
  }
</style>
